import React from 'react';
import { Link } from 'react-router-dom';
import { 
  CheckIcon, 
  ArrowRightIcon,
  ComputerDesktopIcon,
  CogIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  ClockIcon,
  DocumentTextIcon,
  QrCodeIcon,
  ServerIcon
} from '@heroicons/react/24/outline';

export const DICOMFilesPage = () => {
  const automationFeatures = [
    {
      icon: <CogIcon className="h-8 w-8" />,
      title: "Automatización Completa del Flujo DICOM",
      benefits: [
        "Integración automática de dispositivos médicos a PACS",
        "Procesamiento 24/7 sin intervención humana",
        "Reducción de trabajo manual en un 80-90%",
        "Eliminación de errores humanos en la conversión"
      ]
    },
    {
      icon: <ChartBarIcon className="h-8 w-8" />,
      title: "Gestión Inteligente de Dispositivos",
      benefits: [
        "Monitoreo automático de equipos médicos conectados",
        "Matching inteligente de estudios con pacientes",
        "Clasificación automática por modalidad y estado",
        "Gestión de errores y reintentos automáticos"
      ]
    },
    {
      icon: <ShieldCheckIcon className="h-8 w-8" />,
      title: "Cumplimiento y Seguridad DICOM",
      benefits: [
        "Conformidad completa con estándares DICOM 2023b",
        "Trazabilidad completa de todos los estudios",
        "Auditoría automática de procesos y accesos",
        "Seguridad de datos médicos con encriptación"
      ]
    }
  ];

  const commercialFeatures = [
    {
      icon: <ServerIcon className="h-8 w-8" />,
      title: "Gestión de Worklist DICOM",
      description: "Integración completa con servidores DICOM para gestión de estudios",
      features: [
        "Consulta automática C-FIND a servidores DICOM",
        "Filtrado avanzado por modalidad, paciente y fecha",
        "Registro automático de estudios en base de datos",
        "Sincronización bidireccional con HIS/RIS"
      ]
    },
    {
      icon: <DocumentTextIcon className="h-8 w-8" />,
      title: "Procesamiento Inteligente de Documentos",
      description: "Conversión automática de documentos a DICOM encapsulado",
      features: [
        "OCR avanzado para reconocimiento de texto",
        "Matching automático con datos de pacientes",
        "Conversión a DICOM Secondary Capture",
        "Soporte para múltiples formatos (PDF, TIFF, JPEG)"
      ]
    },
    {
      icon: <ComputerDesktopIcon className="h-8 w-8" />,
      title: "Integración PACS Avanzada",
      description: "Comunicación robusta con sistemas PACS y almacenamiento",
      features: [
        "Envío automático C-STORE a servidores PACS",
        "Manejo de errores y reintentos con backoff exponencial",
        "Clasificación automática por modalidad y prioridad",
        "Monitoreo en tiempo real de conexiones DICOM"
      ]
    },
    {
      icon: <QrCodeIcon className="h-8 w-8" />,
      title: "Sistema de Identificación QR",
      description: "Generación y gestión de códigos QR para trazabilidad",
      features: [
        "Generación automática de códigos QR DICOM",
        "Configuración personalizable de campos DICOM",
        "Integración con metadatos de pacientes",
        "Interfaz web para consulta de información"
      ]
    }
  ];

  const economicBenefits = [
    {
      category: "Reducción de Costos",
      icon: <ChartBarIcon className="h-6 w-6" />,
      benefits: [
        "Menos personal necesario para procesamiento manual",
        "Eliminación de errores costosos",
        "Reducción de tiempo de procesamiento",
        "Menor necesidad de capacitación especializada"
      ]
    },
    {
      category: "Aumento de Productividad",
      icon: <ClockIcon className="h-6 w-6" />,
      benefits: [
        "Procesamiento instantáneo de documentos",
        "Disponibilidad 24/7 del sistema",
        "Integración automática con workflows existentes",
        "Notificaciones en tiempo real del estado"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-indigo-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              DICOMFiles
              <span className="text-blue-600 block">Automatización Completa</span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Sistema avanzado de automatización para el procesamiento de documentos DICOM. 
              Integre todo su parque de dispositivos médicos a su sistema PACS.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="border border-blue-600 text-blue-600 px-8 py-3 rounded-lg text-lg font-medium hover:bg-blue-50 transition-colors duration-200"
              >
                Solicitar Demo
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Características Principales
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              DICOMFiles automatiza completamente el flujo de trabajo DICOM, 
              integrando dispositivos médicos con sistemas PACS de forma transparente.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {automationFeatures.map((feature, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-8">
                <div className="text-blue-600 mb-6 flex justify-center">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4 text-center">
                  {feature.title}
                </h3>
                <ul className="space-y-3">
                  {feature.benefits.map((benefit, benefitIndex) => (
                    <li key={benefitIndex} className="flex items-start">
                      <CheckIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Commercial Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Funcionalidades Comerciales Específicas
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Herramientas especializadas para integración completa de dispositivos médicos 
              con sistemas PACS y gestión automatizada de estudios DICOM.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {commercialFeatures.map((feature, index) => (
              <div key={index} className="bg-white rounded-lg shadow-lg p-8">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600">
                      {feature.icon}
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 mb-4">
                      {feature.description}
                    </p>
                    <ul className="space-y-2">
                      {feature.features.map((item, itemIndex) => (
                        <li key={itemIndex} className="flex items-start">
                          <CheckIcon className="h-4 w-4 text-green-500 mr-2 mt-1 flex-shrink-0" />
                          <span className="text-sm text-gray-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Economic Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Beneficios Económicos para el Cliente
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              DICOMFiles no solo mejora la eficiencia, sino que también 
              proporciona un retorno de inversión significativo.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {economicBenefits.map((benefit, index) => (
              <div key={index} className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-8">
                <div className="flex items-center mb-6">
                  <div className="h-12 w-12 bg-blue-600 rounded-lg flex items-center justify-center text-white mr-4">
                    {benefit.icon}
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    {benefit.category}
                  </h3>
                </div>
                <ul className="space-y-4">
                  {benefit.benefits.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-start">
                      <CheckIcon className="h-6 w-6 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ROI Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Retorno de Inversión Comprobado
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Los centros médicos que implementan DICOMFiles reportan mejoras 
                significativas en eficiencia y reducción de costos operativos.
              </p>
              <div className="space-y-6">
                <div className="flex items-center">
                  <div className="text-4xl font-bold text-green-600 mr-4">80-90%</div>
                  <div className="text-gray-700">Reducción en trabajo manual</div>
                </div>
                <div className="flex items-center">
                  <div className="text-4xl font-bold text-blue-600 mr-4">24/7</div>
                  <div className="text-gray-700">Procesamiento automático</div>
                </div>
                <div className="flex items-center">
                  <div className="text-4xl font-bold text-purple-600 mr-4">100%</div>
                  <div className="text-gray-700">Cumplimiento DICOM</div>
                </div>
              </div>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-6 text-center">
                Casos de Uso Típicos
              </h3>
              <div className="space-y-4">
                <div className="border-l-4 border-blue-500 pl-4">
                  <h4 className="font-semibold text-gray-900">Hospitales Grandes</h4>
                  <p className="text-gray-600 text-sm">Procesamiento de 1000+ documentos diarios</p>
                </div>
                <div className="border-l-4 border-green-500 pl-4">
                  <h4 className="font-semibold text-gray-900">Clínicas Especializadas</h4>
                  <p className="text-gray-600 text-sm">Automatización de workflows específicos</p>
                </div>
                <div className="border-l-4 border-purple-500 pl-4">
                  <h4 className="font-semibold text-gray-900">Centros de Diagnóstico</h4>
                  <p className="text-gray-600 text-sm">Integración con múltiples equipos</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            ¿Listo para automatizar tu flujo DICOM?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Descubre cómo DICOMFiles puede transformar la eficiencia 
            de tu centro médico y reducir costos operativos.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="border border-white text-white px-8 py-3 rounded-lg text-lg font-medium hover:bg-blue-700 transition-colors duration-200"
            >
              Solicitar Demo Personalizada
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
